'use client';

// import Image from 'next/image';
import React from 'react';
import { ContainerLG, useModal } from '..';
import { Button, I_playfair } from './StyleConstants';
import { CustomImage as Image } from '..';
import styles from './ColorCatalogueSection.module.css';
import { JwelXIcon } from 'assets/icons/JwelX';
import { CrossCircle } from 'assets/icons';

type ModalContentType = {
  title: React.ReactNode;
  image: string;
  subtitle: React.ReactNode;
  image2: string;
  description: React.ReactNode;
  color: string;
  footerComponent?: React.ReactNode;
};

const CatalogueData: ReadonlyArray<
  Record<string, string | ModalContentType | undefined | null>
> = [
  {
    title: 'Desert Rose',
    image: 'web_v2/rare/portrait-shots/dessert-rose.png',
    modalContent: {
      title: (
        <>
          DESERT R<I_playfair>O</I_playfair>SE
        </>
      ),
      color: '#5D2F17',
      image: 'web_v2/rare/emerging-renders/dessert-rose.jpg',
      subtitle: (
        <>
          THE HARMONY <I_playfair>O</I_playfair>F SAND AND BL
          <I_playfair>O</I_playfair>
          <I_playfair>O</I_playfair>M
        </>
      ),
      image2: 'web_v2/rare/elements-renders/dessert-rose.jpg',
      description: (
        <>
          The ring&apos;s color and design are inspired by the Desert Rose, an
          intricate rose-like formation of crystal clusters of gypsum or baryte,
          which include abundant sand grains. The rosette crystal habit tends to
          occur when the crystals form in arid sandy conditions, such as the
          evaporation of a shallow salt basin. The crystals form a circular
          array of flat plates, giving the rock a shape similar to a rose
          blossom.
        </>
      ),
      footerComponent: (
        <>
          This ring is expertly crafted from 18K Rose Gold. The gold has been
          sourced from London Bullion Market Association (LBMA) approved
          refineries and hallmarked by the Bureau of Indian Standards (BIS).
          <br />
          <br />
          Both the LBMA and BIS adhere to international standards, ensuring the
          highest level of authenticity and quality in precious metals.
        </>
      ),
    },
  },
  {
    title: 'Desert Snow',
    image: 'web_v2/rare/portrait-shots/dessert-snow.png',
    modalContent: {
      title: (
        <>
          DESERT SN<I_playfair>O</I_playfair>W
        </>
      ),
      color: '#0B152C',
      image: 'web_v2/rare/emerging-renders/dessert-snow.jpg',
      subtitle: (
        <>
          ELEGANCE IN FR<I_playfair>O</I_playfair>ST AND LIGHT
        </>
      ),
      image2: 'web_v2/rare/elements-renders/dessert-snow-v2.png',
      description: (
        <>
          Desert Snow is an ode to the rare and mesmerizing sight of snow
          blanketing the desert landscape. This ring features a sleek, frosted
          finish that evokes the delicate beauty of snowflakes, contrasting
          elegantly with the rugged desert environment. The design incorporates
          subtle, shimmering elements that catch the light, mimicking the
          sparkle of snow under the sun. All wrapped in a stunningly crafted
          piece that balances beauty and sophistication.
        </>
      ),
      footerComponent: (
        <>
          The platinum jewellery described herein and bearing the PT950 purity
          mark has been crafted by a Platinum Guild (I) Pvt. Ltd. (India)
          authorised manufacturer, observing the Platinum Guild (I) Pvt. Ltd.
          purity assurance scheme, audited by TrustEver Assurance Services LLP.
        </>
      ),
    },
  },
  {
    title: 'Dune',
    image: 'web_v2/rare/portrait-shots/dune.png',
    modalContent: {
      title: <>DUNE</>,
      color: '#4C190C',
      image: 'web_v2/rare/emerging-renders/dune.jpg',
      subtitle: (
        <>
          P<I_playfair>O</I_playfair>ETRY <I_playfair>O</I_playfair>F SHIFTING
          SANDS
        </>
      ),
      image2: 'web_v2/rare/elements-renders/dune.jpg',
      description: (
        <>
          Dune captures the essence of the ever-shifting sands of the desert.
          Its design showcases finely crafted grooves that flow gracefully
          around the band, resembling the wind&apos;s gentle caress across a
          dune. The meticulous brushed finish enhances its tactile appeal,
          offering a smooth yet textured feel.
        </>
      ),
      footerComponent: (
        <>
          This ring is expertly crafted from 18K Yellow Gold. The gold has been
          sourced from London Bullion Market Association (LBMA) approved
          refineries and hallmarked by the Bureau of Indian Standards (BIS).
          <br />
          <br />
          Both the LBMA and BIS adhere to international standards, ensuring the
          highest level of authenticity and quality in precious metals.
        </>
      ),
    },
  },
];

const ModalContentTemplate = (
  props: { className?: string } & ModalContentType,
) => {
  const ModalClient = useModal();

  return (
    <div
      className={styles.modalContent}
      style={
        { ['--modal-bg']: props.color ?? '#000000' } as React.CSSProperties
      }
    >
        <h2>{props.title}</h2>
        <Image width="1800" height="940" src={props.image} alt={''} />
        {props.subtitle ? <p className="subtitle">{props.subtitle}</p> : null}
        {props.image2 ? (
          <Image
            width="1080"
            height="743"
            src={props.image2}
            alt={''}
            className="image-2"
          />
        ) : null}
        {props.description ? (
          <p className="description">{props.description}</p>
        ) : null}

        <div className="modal-footer">
          <div className="trademark">
            <div className="auth-stamp-img">
              <Image
                width={88.5}
                height={88.5}
                src={'web_v2/rare/auth-stamp.png'}
                alt={'auth-stamp'}
                className="image-2"
              />
            </div>
          </div>
          {props.footerComponent ? (
            <div className="">
              <p className="footer-description">{props.footerComponent}</p>
            </div>
          ) : null}
          <div className="partner">
            <p className="text">in partnership with</p>
            <JwelXIcon />
          </div>
        </div>

        <div
          style={{
            textAlign: 'center',
            position: 'absolute',
            top: '24px',
            right: '24px',
          }}
        >
          <button
            className="close-btn"
            onClick={() => {
              ModalClient.hide();
              ModalClient.set(null);
            }}
          >
            <CrossCircle />
          </button>
        </div>
      </div>
    );
};

export const ColorCatalogueSection = ({
  head,
  backgroundColor,
  descriptionAlignment,
}: {
  head?: string;
  backgroundColor?: string;
  descriptionAlignment?: string;
}) => {
  const ModalClient = useModal();

    const openModalWith = (component: React.ReactNode) => {
      ModalClient.set(component);
      ModalClient.setCloseButtonTheme('light');
      ModalClient.setCloseBtnHidden(true);
      ModalClient.show();
    };

    return (
      <section
        className={styles.root}
        style={{
          backgroundColor: backgroundColor ? backgroundColor : 'transparent',
        }}
      >
        <ContainerLG>
          {head ? <h2 className="head">{head} </h2> : null}
          {/* <Container> */}
          <p
            className="init-description"
            style={{
              textAlign: (descriptionAlignment as any) || 'left',
              maxWidth: descriptionAlignment === 'center' ? '52ch' : '48ch',
              margin: descriptionAlignment === 'center' ? '0 auto' : 'initial',
              paddingBottom:
                descriptionAlignment === 'center' ? '56px' : '16px',
            }}
          >
            Rare transforms natural wonders into exquisite masterpieces,
            reflecting the extraordinary elegance and sophistication present in
            nature.
          </p>
          <div className="content">
            {/* <H2Caption>RARE COLLECTION</H2Caption> */}
            <CardsContainer>
              {CatalogueData.map((data, index) => (
                <Card
                  key={index}
                  onClick={() =>
                    openModalWith(
                      <ModalContentTemplate
                        {...(data.modalContent as ModalContentType)}
                      />,
                    )
                  }
                  aria-hidden="true"
                >
                  <div className="img-container">
                    <Image
                      width="1080"
                      height="1424"
                      src={data.image as string}
                      alt={data.title as string}
                    />
                  </div>
                  <Button
                    style={{
                      fontWeight: '500',
                    }}
                  >
                    {data.title as string}
                  </Button>
                </Card>
              ))}
            </CardsContainer>
          </div>
        </ContainerLG>
        {/* </Container> */}
      </section>
    );
};

const CardsContainer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`${styles.cardsContainer} u-hide-scrollbar${
      className ? ` ${className}` : ""
    }`}
    {...props}
  />
);

const Card = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`${styles.card}${className ? ` ${className}` : ""}`}
    {...props}
  />
);
